/**
 * Localizers and scene placements (TraceBackend#999).
 *
 * A **localizer** is a team-library object a device can observe to learn
 * its pose in the localizer's own frame `L`. Kind `image` is a printed
 * marker whose frame is the one the viewer has always used for
 * image anchors (`behaviors/imageanchor.ts` header) — origin at the image
 * centre, `+X` image-right, `+Y` image-top, `+Z` out of the face, metres.
 * Kind `groma` is a VPS space whose frame is its space document's root
 * (`displayAssetId`); the viewer draws and edits its placements in an editor
 * and never localizes against it.
 *
 * A **placement** links one localizer to one scene with a rigid
 * `P = T_scene←L` (translation in metres + unit quaternion `[x, y, z, w]`,
 * no scale). The scene frame is glTF's: right-handed, `+Y` up, metres — in
 * the editor it is the editor world. Seeing the marker at `T_world←L` gives
 * `T_world←scene = T_world←L · P⁻¹`.
 *
 * **How the viewer uses the primary placement (phase 1).** Scene content
 * hangs off the anchor host (the session's document root, else the engine
 * root) exactly as it always has, and the host is posed at `P` of the
 * primary placement. That is the legacy image-anchor path with the pose
 * read from `P` instead of derived from `Orientation`/`PitchDegrees`: the
 * legacy path poses the host at `Rx(θ)`, so a migrated placement carries
 * `q = Rx(θ)` and renders every migrated scene exactly as before
 * ({@link legacyPlacementRotation}; golden vectors in
 * `test/vitest/fixtures/localizer-legacy-rotation.golden.json`).
 *
 * Types here are minimal structural POJOs (same pattern as
 * `CmsSceneInput`) so consumers' generated DTOs satisfy them without this
 * package importing any CMS types. Pure apart from Babylon quaternion math.
 */

import { Quaternion } from '@babylonjs/core/Maths/math.vector';
import { AnchorOrientation, resolveAnchorPitchRadians } from '../scenedata/common';
import { anchorPitchDependsOnContent } from '../loaders/anchorOrientationHeuristic';

/** `[x, y, z]` in metres. */
export type PlacementTranslation = [number, number, number];
/** Unit quaternion `[x, y, z, w]`. */
export type PlacementRotation = [number, number, number, number];

/**
 * Where a placement's rotation came from. `legacy_heuristic` rows were
 * migrated from a `Free`/unset marker with no pitch, whose pose the viewer
 * has always inferred from the scene content — the backend can't, so the
 * row's `q` is a placeholder and the viewer resolves it (see
 * `LoadCmsSceneResult.anchorPlacement`).
 */
export type PlacementRotationSource = 'explicit' | 'legacy_heuristic';

/** Wire value for an image-marker localizer. */
export const IMAGE_LOCALIZER_KIND = 'image';
/**
 * Wire value for a groma VPS space. Its placements never anchor and are never
 * primary: `effectiveScenePlacements` drops them, selection refuses them, and
 * only an editor draws them.
 */
export const GROMA_LOCALIZER_KIND = 'groma';

/** A rigid transform `T_scene←L`: translation (metres) + unit quaternion. */
export interface PlacementPose {
    t: PlacementTranslation;
    q: PlacementRotation;
}

/** A localizer, as embedded in a placement. */
export interface Localizer {
    id: string;
    /** `'image'` or `'groma'`; other kinds pass through untouched. */
    kind: string;
    name: string;
    /** Marker image URL (kind `image`). */
    imageUrl: string | null;
    /** Printed marker width in metres (kind `image`). */
    widthM: number | null;
    /** The space asset the editor draws for this localizer (kind `groma`). */
    displayAssetId: string | null;
}

/** One (scene, localizer) link with its transform. */
export interface ScenePlacement extends PlacementPose {
    /** Placement id. */
    id: string;
    localizerId: string;
    rotationSource: PlacementRotationSource;
    isPrimary: boolean;
    enabled: boolean;
    /** The embedded localizer; null when the server omitted it. */
    localizer: Localizer | null;
}

/** Structural subset of the CMS localizer DTO. */
export interface CmsLocalizerInput {
    id?: string | null;
    kind?: string | null;
    name?: string | null;
    imageUrl?: string | null;
    widthM?: number | null;
    displayAssetId?: string | null;
}

/** Structural subset of one element of the CMS scene DTO's `localizers`. */
export interface CmsScenePlacementInput {
    id?: string | null;
    localizerId?: string | null;
    t?: readonly number[] | null;
    q?: readonly number[] | null;
    rotationSource?: string | null;
    isPrimary?: boolean | null;
    enabled?: boolean | null;
    /** Embedded localizer metadata; null when the server omitted it. */
    localizer?: CmsLocalizerInput | null;
}

/** Structural subset of a CMS scene record carrying placements. */
export interface CmsLocalizedSceneSource {
    /** Absent or null = an older server: fall back to the legacy fields. */
    localizers?: readonly CmsScenePlacementInput[] | null;
    /**
     * Set by the server when the primary placement can't be projected onto
     * the legacy accessor fields (translated or yawed primary, no image
     * placement): older clients see the scene as unanchored.
     * Informational only: this client reads the placements themselves and
     * never branches on it.
     */
    legacyAnchorDegraded?: boolean | null;
}

/**
 * `PUT /scenes/{id}/localizers?kind=<kind>` body element. One request holds
 * one kind. A groma placement is never primary, and the server stamps its
 * frame epoch.
 */
export interface ScenePlacementWrite {
    localizerId: string;
    t: PlacementTranslation;
    q: PlacementRotation;
    isPrimary: boolean;
    enabled: boolean;
    rotationSource?: PlacementRotationSource;
}

/** `PUT /scenes/{id}/localizers` body. */
export interface ScenePlacementsWriteRequest {
    placements: ScenePlacementWrite[];
}

const IDENTITY_ROTATION: PlacementRotation = [0, 0, 0, 1];

/**
 * Parse the scene DTO's `localizers` into placements.
 *
 * Returns `null` when the field is absent or null — an older server, whose
 * scenes anchor from the legacy fields — and an array (possibly empty)
 * otherwise. Malformed rows (no id, a non-finite translation, a zero or
 * non-finite quaternion) are dropped rather than trusted; quaternions are
 * normalized. A missing `rotationSource` reads as `explicit`, a missing
 * `enabled` as true. The removed `tracking` wire field is ignored; sessions
 * always place once and parsed placements carry no tracking-mode setting.
 *
 * To decide the scene's anchor, use `effectiveScenePlacements`
 * (`cms/sceneAnchor.ts`), not this. Before the backend's backfill runs, an
 * empty list next to a legacy `Image` accessor still means the legacy path.
 */
export function parseScenePlacements(
    scene: CmsLocalizedSceneSource | null | undefined,
): ScenePlacement[] | null {
    const rows = scene?.localizers;
    if (rows == null) return null;
    const placements: ScenePlacement[] = [];
    for (const row of rows) {
        const placement = parsePlacement(row);
        if (placement) placements.push(placement);
    }
    return placements;
}

function parsePlacement(row: CmsScenePlacementInput | null | undefined): ScenePlacement | null {
    if (!row?.id) return null;
    const localizerId = row.localizerId ?? row.localizer?.id;
    if (!localizerId) return null;
    const t = parseTranslation(row.t);
    const q = normalizeRotation(row.q);
    if (!t || !q) return null;
    return {
        id: row.id,
        localizerId,
        t,
        q,
        rotationSource: row.rotationSource === 'legacy_heuristic' ? 'legacy_heuristic' : 'explicit',
        isPrimary: row.isPrimary === true,
        enabled: row.enabled !== false,
        localizer: parseLocalizer(row.localizer, localizerId),
    };
}

function parseLocalizer(input: CmsLocalizerInput | null | undefined, fallbackId: string): Localizer | null {
    if (!input) return null;
    const width = input.widthM;
    return {
        id: input.id ?? fallbackId,
        kind: input.kind ?? IMAGE_LOCALIZER_KIND,
        name: input.name ?? '',
        imageUrl: input.imageUrl || null,
        widthM: typeof width === 'number' && Number.isFinite(width) && width > 0 ? width : null,
        displayAssetId: input.displayAssetId || null,
    };
}

function parseTranslation(t: readonly number[] | null | undefined): PlacementTranslation | null {
    if (t == null) return [0, 0, 0];
    if (t.length !== 3 || !t.every(Number.isFinite)) return null;
    return [t[0], t[1], t[2]];
}

/**
 * Normalize a quaternion; null for a zero, non-finite or wrongly sized one.
 * Absent reads as identity. A quaternion that is already unit to 1e-12 is
 * returned as given, so values round-trip bit-for-bit.
 */
export function normalizeRotation(q: readonly number[] | null | undefined): PlacementRotation | null {
    if (q == null) return [...IDENTITY_ROTATION];
    if (q.length !== 4 || !q.every(Number.isFinite)) return null;
    const len = Math.hypot(q[0], q[1], q[2], q[3]);
    if (len < 1e-12) return null;
    // Already unit (the server normalizes on write): keep the exact values.
    if (Math.abs(len - 1) < 1e-12) return [q[0], q[1], q[2], q[3]];
    return [q[0] / len, q[1] / len, q[2] / len, q[3] / len];
}

/**
 * The placement a single-image session anchors from: the enabled primary
 * placement of an image localizer that has an image. Undefined when there
 * is none — the scene is then unanchored, whatever the legacy fields say.
 */
export function findAnchoringPlacement(
    placements: readonly ScenePlacement[] | null | undefined,
): ScenePlacement | undefined {
    return placements?.find(p => p.isPrimary && p.enabled && isImagePlacement(p));
}

function isImagePlacement(p: ScenePlacement): boolean {
    return p.localizer?.kind === IMAGE_LOCALIZER_KIND && !!p.localizer.imageUrl;
}

/** Map a string from the CMS Orientation enum to AnchorOrientation (unknown → Free). */
export function parseAnchorOrientation(value: string | null | undefined): AnchorOrientation {
    switch (value) {
        case AnchorOrientation.Horizontal: return AnchorOrientation.Horizontal;
        case AnchorOrientation.Vertical:   return AnchorOrientation.Vertical;
        default:                            return AnchorOrientation.Free;
    }
}

/** `Rx(pitch)` as the viewer builds it for the anchor host, as a tuple. */
export function placementRotationFromPitch(pitchRadians: number): PlacementRotation {
    const q = Quaternion.RotationYawPitchRoll(0, pitchRadians, 0);
    return [q.x, q.y, q.z, q.w];
}

/** One row of the legacy → placement migration table. */
export interface LegacyPlacementRotation {
    /** `T_scene←L` rotation; null when the pose depends on the content. */
    q: PlacementRotation | null;
    rotationSource: PlacementRotationSource;
}

/**
 * The placement rotation that reproduces the legacy image-anchor pose for
 * an accessor's `Orientation` / `PitchDegrees` — the migration table of
 * TraceBackend#999, derived from the code that poses the host today.
 *
 * The legacy path poses the anchor host (and the marker preview on it) at
 * `Rx(θ)` in the editor world, `θ` from `resolveAnchorPitchRadians`. The
 * editor world is the scene frame, so `T_scene←L = Rx(θ)`:
 *
 *   - `Vertical` → identity; `Horizontal` → `Rx(−π/2)` = `[−√½, 0, 0, √½]`
 *     (the marker's `+Z` normal maps to scene `+Y`: it lies flat).
 *   - `Free`/unset with pitch `p ≠ 0` → `Rx(+p°)`.
 *   - `Free`/unset with pitch 0/null → `{ q: null, rotationSource:
 *     'legacy_heuristic' }`: the content decides
 *     (`resolveAnchorPitchRadiansForRoot`).
 *
 * Note the sign: `q = Rx(+θ)`, not `Rx(−θ)`.
 */
export function legacyPlacementRotation(
    orientation: string | null | undefined,
    pitchDegrees: number | null | undefined,
): LegacyPlacementRotation {
    const parsed = orientation == null ? undefined : parseAnchorOrientation(orientation);
    const pitch = pitchDegrees ?? undefined;
    if (anchorPitchDependsOnContent(parsed, pitch)) {
        return { q: null, rotationSource: 'legacy_heuristic' };
    }
    return {
        q: placementRotationFromPitch(resolveAnchorPitchRadians(parsed, pitch)),
        rotationSource: 'explicit',
    };
}

/** Tolerance for "this rotation is a pure pitch" and "t is zero". */
const PROJECTION_EPSILON = 1e-6;

/** Legacy accessor fields equivalent to a placement, when there are any. */
export interface LegacyAnchorProjection {
    orientation: AnchorOrientation;
    /** Degrees; 0 for `Vertical`/`Horizontal`. */
    pitchDegrees: number;
}

/**
 * Project a placement onto the legacy accessor fields — the inverse of
 * {@link legacyPlacementRotation}. Null when it isn't expressible: a
 * non-zero translation, or a rotation that isn't a pure pitch `Rx(φ)`.
 *
 *   - `φ = 0` → `Vertical`
 *   - `φ = −π/2` → `Horizontal`
 *   - otherwise → `Free` with `pitchDegrees = φ` in degrees, `φ ∈ (−180, 180]`.
 */
export function projectPlacementToLegacyAnchor(pose: PlacementPose): LegacyAnchorProjection | null {
    if (pose.t.some(v => Math.abs(v) > PROJECTION_EPSILON)) return null;
    const q = normalizeRotation(pose.q);
    if (!q) return null;
    let [x, y, z, w] = q;
    if (Math.abs(y) > PROJECTION_EPSILON || Math.abs(z) > PROJECTION_EPSILON) return null;
    // q and −q are the same rotation; pick w ≥ 0 so φ lands in [−π, π].
    if (w < 0) { x = -x; w = -w; }
    const phi = 2 * Math.atan2(x, w);
    const degrees = phi * 180 / Math.PI;
    if (Math.abs(phi) < PROJECTION_EPSILON) {
        return { orientation: AnchorOrientation.Vertical, pitchDegrees: 0 };
    }
    if (Math.abs(phi + Math.PI / 2) < PROJECTION_EPSILON) {
        return { orientation: AnchorOrientation.Horizontal, pitchDegrees: 0 };
    }
    return {
        orientation: AnchorOrientation.Free,
        pitchDegrees: Math.abs(degrees + 180) < PROJECTION_EPSILON ? 180 : degrees,
    };
}

/**
 * Build the `PUT /scenes/{id}/localizers` body from placements. A
 * `legacy_heuristic` row goes back as such only while the caller hasn't
 * resolved it — pass `resolved` (placement id → resolved rotation) to write
 * the viewer's resolution back as `explicit`, per TraceBackend#999.
 */
export function toScenePlacementsWriteRequest(
    placements: readonly ScenePlacement[],
    resolved: ReadonlyMap<string, PlacementRotation> = new Map(),
): ScenePlacementsWriteRequest {
    return {
        placements: placements.map(p => {
            const resolvedQ = resolved.get(p.id);
            return {
                localizerId: p.localizerId,
                t: [...p.t],
                q: resolvedQ ? [...resolvedQ] : [...p.q],
                isPrimary: p.isPrimary,
                enabled: p.enabled,
                rotationSource: resolvedQ ? 'explicit' : p.rotationSource,
            };
        }),
    };
}
